import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Row, Col,
} from 'antd';

import {
  FontSizeOutlined,
  BorderOutlined,
  PictureOutlined,
  TableOutlined,
  FileAddOutlined,
  DeleteOutlined,
  CopyOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import PDFDesignerToolbarButton from './PDFDesignerToolbarButton';

import CustomConfirmModal from '../../modals/CustomConfirmModal';
import OnTraccrSwitch from '../../inputs/OnTraccrSwitch';
import OnTraccrCheckbox from '../../inputs/OnTraccrCheckbox';

const showModal = (onDelete) => () => {
  CustomConfirmModal({
    title: 'Delete this page?',
    content: 'Deleting a page will delete all items on the page.',
    okText: 'Delete',
    cancelText: 'Cancel',
    onOk: onDelete,
    zIndex: 1300,
  });
};

const iconStyle = { marginLeft: 0 };

function PDFDesignerToolbar({
  onItemAdd,
  numberOfPages,
  onPageDeleted,
  onTopOfPageChecked,
  startAtTop,
  useStandardTemplate,
  onUseStandardTemplateChange,
  isSectionDuplicationEnabled,
  embeddedLayoutEditor,
}) {
  const onClick = useCallback((type) => () => onItemAdd(type), [onItemAdd]);
  const onCheckChanged = useCallback((checked) => {
    onTopOfPageChecked(checked);
  }, [onTopOfPageChecked]);

  const onSwitch = useCallback((e) => {
    onUseStandardTemplateChange(e);
  }, [onUseStandardTemplateChange]);

  const showCustomTools = embeddedLayoutEditor || !useStandardTemplate;

  const className = embeddedLayoutEditor ? 'pdf-designer-toolbar-container-embedded' : 'pdf-designer-toolbar-container';

  return (
    <div
      className={className}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{
          height: '100%', width: '100%', paddingLeft: 20, paddingRight: 0,
        }}
        gutter={20}
      >
        <Col style={{ padding: 0 }}>
          <Row justify="start" align="middle">
            {!embeddedLayoutEditor && (
              <Col>
                <Row justify="start" align="middle">
                  <OnTraccrSwitch
                    value={useStandardTemplate}
                    onChange={onSwitch}
                    title={useStandardTemplate ? 'Standard' : 'Custom'}
                  />
                </Row>
              </Col>
            )}
            {showCustomTools && (
              <Col className="pdf-toolbar">
                <Row justify="start" align="middle">
                  <PDFDesignerToolbarButton
                    message="Add Text"
                    onClick={onClick('text')}
                  >
                    <FontSizeOutlined style={iconStyle} />
                  </PDFDesignerToolbarButton>

                  <PDFDesignerToolbarButton
                    message="Add Rectangle"
                    onClick={onClick('rectangle')}
                  >
                    <BorderOutlined style={iconStyle} />
                  </PDFDesignerToolbarButton>

                  <PDFDesignerToolbarButton
                    message="Add Image"
                    onClick={onClick('image')}
                  >
                    <PictureOutlined style={iconStyle} />
                  </PDFDesignerToolbarButton>

                  <PDFDesignerToolbarButton
                    message="Add Table"
                    onClick={onClick('table')}
                  >
                    <TableOutlined style={iconStyle} />
                  </PDFDesignerToolbarButton>
                  <PDFDesignerToolbarButton
                    message="Add Blank Page"
                    onClick={onClick('page')}
                  >
                    <FileAddOutlined style={iconStyle} />
                  </PDFDesignerToolbarButton>
                  {!embeddedLayoutEditor && (
                    <PDFDesignerToolbarButton
                      message="Copy Page"
                      onClick={onClick('copyPage')}
                    >
                      <CopyOutlined style={iconStyle} />
                    </PDFDesignerToolbarButton>
                  )}
                  {isSectionDuplicationEnabled && (
                    <PDFDesignerToolbarButton
                      message="Add Repeatable Section"
                      onClick={onClick('repeatableSection')}
                    >
                      <AppstoreAddOutlined style={iconStyle} />
                    </PDFDesignerToolbarButton>
                  )}
                </Row>
              </Col>
            )}
          </Row>
        </Col>
        {showCustomTools && (
          <Col style={{ padding: 0 }}>
            <Row justify="end" align="middle">
              {!embeddedLayoutEditor && (
                <OnTraccrCheckbox
                  value={startAtTop}
                  onChange={onCheckChanged}
                  label="Start at top of page?"
                  hoverNode={(
                    <div style={{ maxWidth: 250 }}>
                      Check this box if you want the contents of this page
                      to always start at the top of a new page
                    </div>
                  )}
                  helpPlacement="bottomRight"
                />
              )}
              {numberOfPages > 1
              && (
              <PDFDesignerToolbarButton
                message="Delete Page"
                onClick={showModal(onPageDeleted)}
                placement="bottomLeft"
                style={{ marginLeft: 20 }}
              >
                <DeleteOutlined style={{ ...iconStyle, color: 'red' }} />
              </PDFDesignerToolbarButton>
              )}
            </Row>
          </Col>
        )}
      </Row>
    </div>
  );
}

PDFDesignerToolbar.propTypes = {
  onItemAdd: PropTypes.func.isRequired,
  numberOfPages: PropTypes.number.isRequired,
  onPageDeleted: PropTypes.func.isRequired,
  onTopOfPageChecked: PropTypes.func.isRequired,
  startAtTop: PropTypes.bool.isRequired,
  useStandardTemplate: PropTypes.bool.isRequired,
  onUseStandardTemplateChange: PropTypes.func.isRequired,
  isSectionDuplicationEnabled: PropTypes.bool,
  embeddedLayoutEditor: PropTypes.bool,
};

PDFDesignerToolbar.defaultProps = {
  isSectionDuplicationEnabled: false,
  embeddedLayoutEditor: false,
};

export default PDFDesignerToolbar;
